import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import * as R from 'ramda'

import FeatureToggle from '~/constants/featureToggle'
import { useGetChargeSheetId } from '~/store/hooks/chargeSheet'
import { useLogItemStateGetter, useRefillDialog } from '~/store/hooks/orders'
import { useCreateOrUpdatePrescription } from '~/store/hooks/prescription'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getOrdersIsSending } from '~/store/reducers/orders'
import type { CRUDOrderOptions, Order, PrescriptionTimelineItem } from '~/types'
import { timelinePrescriptionToRegularPrescription } from '~/utils/prescriptionUtils'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'

/**
 * Hook that prepares and returns a callback to open the refill/dispense dialog
 * for a given prescription timeline item. The callback will transform the
 * timeline prescription into a regular prescription, open the refill dialog,
 * and handle creation/updating of the related order on save.
 *
 * @param item - The prescription timeline item for which a refill should be dispensed.
 * @returns A function that, when invoked, opens the refill dialog for the given item.
 */
export const useDispenseRefill = (item: PrescriptionTimelineItem) => {
  const { prescription } = item
  const { clientId } = prescription
  const isFoodCatalogEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOOD_CATALOG),
  )
  const chargeSheetId = useGetChargeSheetId(clientId)
  const [openRefillDialog, closeRefillDialog] = useRefillDialog()
  const logItemStateGetter = useLogItemStateGetter()
  const createOrUpdatePrescription = useCreateOrUpdatePrescription()

  const closeRefillDialogAfterSave = useCloseAfterCreation(
    closeRefillDialog,
    getOrdersIsSending,
  )

  const chargeSheetCreateRefill = useCallback(
    (refill: Order, options: CRUDOrderOptions) => {
      createOrUpdatePrescription(logItemStateGetter(refill)!, {
        ...options,
        outsideSoap: true,
        invoiceId: chargeSheetId,
      })
      if (options.print) {
        closeRefillDialogAfterSave()
      }
    },
    [
      createOrUpdatePrescription,
      logItemStateGetter,
      chargeSheetId,
      closeRefillDialogAfterSave,
    ],
  )

  const handleDispenseRefill = useCallback(() => {
    const refillItem = timelinePrescriptionToRegularPrescription(
      prescription,
      item,
      isFoodCatalogEnabled,
    )
    openRefillDialog({
      outsideSoap: true,
      isInvoice: false,
      prescription: {
        ...R.omit(['id', 'soapId'], refillItem),
        parent: { ...R.omit(['soapId'], refillItem) },
      },
      onSave: chargeSheetCreateRefill,
    })
  }, [
    prescription,
    item,
    isFoodCatalogEnabled,
    openRefillDialog,
    chargeSheetCreateRefill,
  ])

  return handleDispenseRefill
}
